import { useEffect, useState } from "react";
import { Mail, MapPin, MessageCircle, Check } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { SEO, Reveal, Eyebrow } from "@/components/motion";
import { PROPERTY_TYPES } from "@/data/content";

const inputCls =
  "w-full rounded-xl border border-subtle bg-surface px-4 py-3.5 text-sm text-ink outline-none transition-colors focus:border-blue placeholder:text-faint/60";

export default function Contact() {
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", service: "", property_type: "", preferred_date: "", message: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await api.post("/contact", form);
      setStatus("sent");
    } catch (err) {
      setError(apiError(err));
      setStatus("idle");
    }
  };

  return (
    <main data-testid="contact-page">
      <SEO
        title="Contact HOMELARA | Cleaning Services in London Zones 1 & 2"
        description="Have a question or need help choosing a cleaning service? Contact HOMELARA - professional cleaning across London Zones 1 & 2. Email lauraflorez@homelara.uk or message us on WhatsApp."
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-32 sm:pt-44 pb-16">
        <Reveal>
          <Eyebrow>Contact</Eyebrow>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink">
            Let's talk about your space.
          </h1>
          <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-body">
            Have a question, need help choosing a service or looking for something more specific?
            Get in touch and we'll be happy to help.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 sm:pb-32">
        <div className="grid gap-10 lg:grid-cols-[1fr_0.65fr]">
          <Reveal>
            {status === "sent" ? (
              <div data-testid="contact-success" className="rounded-3xl border border-sage/50 bg-sage-light/50 p-10 sm:p-14 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue text-canvas">
                  <Check size={24} />
                </span>
                <h2 className="mt-6 font-serif text-3xl tracking-tight text-ink">Message received.</h2>
                <p className="mx-auto mt-3 max-w-sm text-body">
                  Thank you, {form.name.split(" ")[0] || "there"}. We've sent a confirmation to your email and
                  we'll get back to you as soon as we can - usually within one working day.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-3xl border border-subtle bg-surface p-7 sm:p-10" data-testid="contact-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-faint">Name *</label>
                    <input required data-testid="contact-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your full name" />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-faint">Email *</label>
                    <input required type="email" data-testid="contact-email-input" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-faint">Phone</label>
                    <input data-testid="contact-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+44 ..." />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-faint">Service required</label>
                    <select data-testid="contact-service-select" className={inputCls} value={form.service} onChange={set("service")}>
                      <option value="">Select a service</option>
                      {services.map((s) => (
                        <option key={s.slug} value={s.name}>{s.name}</option>
                      ))}
                      <option value="Not sure yet">Not sure yet</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-faint">Property type</label>
                    <select data-testid="contact-property-select" className={inputCls} value={form.property_type} onChange={set("property_type")}>
                      <option value="">Select property type</option>
                      {PROPERTY_TYPES.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-faint">Preferred date</label>
                    <input type="date" data-testid="contact-date-input" className={inputCls} value={form.preferred_date} onChange={set("preferred_date")} />
                  </div>
                </div>
                <div className="mt-5">
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-faint">Message *</label>
                  <textarea required rows={5} data-testid="contact-message-input" className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us about your space and what you need..." />
                </div>
                {error && <p data-testid="contact-error" className="mt-4 text-sm text-red-700">{error}</p>}
                <button
                  type="submit"
                  disabled={status === "sending"}
                  data-testid="contact-submit-button"
                  className="mt-7 inline-flex items-center gap-2 rounded-full bg-blue px-9 py-4 font-mono text-xs uppercase tracking-[0.18em] text-canvas transition-all duration-300 hover:bg-blue-hover hover:-translate-y-0.5 disabled:opacity-60"
                >
                  {status === "sending" ? "Sending..." : "Get in Touch"}
                </button>
              </form>
            )}
          </Reveal>

          <Reveal delay={0.1}>
            <aside className="space-y-6">
              <div className="rounded-3xl bg-blue-midnight p-8 text-canvas">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-sage">Prefer to write or chat?</p>
                <a href="mailto:lauraflorez@homelara.uk" data-testid="contact-email-link" className="mt-5 flex items-center gap-3 text-sm hover:text-sage transition-colors">
                  <Mail size={17} className="text-sage" /> lauraflorez@homelara.uk
                </a>
                <a
                  href="https://wa.me/447939365873?text=Hello%20HOMELARA%2C%20I%20would%20like%20to%20ask%20about%20your%20cleaning%20services."
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-link"
                  className="mt-4 flex items-center gap-3 text-sm hover:text-sage transition-colors"
                >
                  <MessageCircle size={17} className="text-sage" /> +44 7939 365873 (WhatsApp)
                </a>
                <p className="mt-4 flex items-center gap-3 text-sm text-canvas/75">
                  <MapPin size={17} className="text-sage" /> London Zones 1 &amp; 2
                </p>
              </div>
              <div className="img-frame aspect-[4/3] overflow-hidden rounded-3xl">
                <img
                  src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1400&q=80"
                  alt="Calm, bright interior of a professionally cleaned London home"
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
            </aside>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
